import styled, { css } from 'styled-components';
import { colorCode, font } from '@styles/styleUtils';
import { CSSProperties } from 'react';
import { motion } from 'framer-motion';

interface ModalWrapProps {
  $style: 'white' | 'black';
  $backgroundImage?: string;
  $padding?: string;
}

export const MotionOverlay = styled(motion.div)`
  background-color: rgba(18, 18, 20, 0.6);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndex.modal};
`;

export const ModalContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal + 1};
`;

export const ModalWrapper = styled.div`
  width: 100%;
  max-width: 480px;
  position: relative;
`;

export const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 12px;
  width: 100%;
  will-change: transform, opacity;
`;

export const BackgroundWrap = styled.div<{ $blackBackdrop: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ $blackBackdrop }) =>
    $blackBackdrop ? 'black' : 'rgba(18, 18, 20, 0.6)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
`;

export const ModalWrap = styled.div<ModalWrapProps>`
  position: relative;
  width: clamp(320px, calc(100% - 40px), 480px);
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  background-color: ${({ $style }) =>
    $style === 'black' ? colorCode('gray80') : colorCode('white')};
  color: ${({ $backgroundImage }) =>
    $backgroundImage ? colorCode('white') : colorCode('gray80')};
  border-radius: 20px;
  padding: ${({ $padding }) => $padding || '40px 16px 28px'};
  align-items: center;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  ${({ $backgroundImage }) =>
    $backgroundImage &&
    css`
      background-image: url('${$backgroundImage}');
      background-size: cover;
      background-position: center;
    `}
`;

export const ModalHeaderWrap = styled.div<{ $style: 'white' | 'black' }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  color: ${({ $style }) =>
    $style === 'black' ? colorCode('white') : colorCode('gray100')};
`;

export const ModalTitleWrap = styled.h2<{ $style: 'white' | 'black' }>`
  ${font('heading2_semiBold')};
  color: ${({ $style }) =>
    $style === 'black' ? colorCode('white') : colorCode('gray100')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  white-space: pre-line;
  text-align: center;

  button {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
  }
`;

export const ModalContentWrap = styled.div<{ $style: 'white' | 'black' }>`
  width: 100%;
  flex: 1;

  &.text {
    ${font('body3')};
    color: ${({ $style }) =>
      $style === 'black' ? colorCode('gray30') : colorCode('gray60')};
    text-align: center;
  }
`;

export const ButtonWrap = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;
  margin-top: 24px;
`;

export const ModalButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${font('body1')};
  border-radius: 12px;
  cursor: pointer;
`;

export const SubmitButton = styled(ModalButton)<{ $style?: 'white' | 'black' }>`
  color: ${({ $style }) =>
    $style === 'black' ? colorCode('gray100') : colorCode('white')};
  background-color: ${({ $style }) =>
    $style === 'black' ? colorCode('white') : colorCode('gray80')};
  padding: 14px 0;
  border: none;
`;

export const CancelButton = styled(ModalButton)<{
  $color: CSSProperties['backgroundColor'];
}>`
  border: 1px solid ${colorCode('gray30')};
  color: ${({ $color }) => ($color ? colorCode('white') : colorCode('gray60'))};
  padding: 13px 0;
  background-color: ${({ $color }) => $color || colorCode('white')};
`;
